import React from "react";
import { useProject } from "../../state/ProjectContext";
import EditableTable from "./_EditableTable";

/**
 * Data Migration tab — object-level tracker with mock load cycles + quality.
 */
export default function DataMigration() {
  const { doc } = useProject();
  const objects = doc?.dataMigration?.objects || [];
  // KPIs computed from objects
  const total = objects.length;
  const ready = objects.filter(o => o.status === "Ready").length;
  const avgError = total ? Math.round(objects.reduce((s, o) => s + (Number(o.errorPct) || 0), 0) / total) : 0;

  return (
    <>
      <div className="mb-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 section-eyebrow">Data Migration</div>
        <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-zinc-900 mt-1">Data Migration</h2>
        <p className="text-sm text-zinc-500 mt-1 max-w-3xl">Object-level tracker · mock load cycles · error rates · quality scorecards · reconciliation.</p>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-5" data-export-capture="data-migration-kpis" data-export-title="Data Migration · KPIs">
        <div className="card p-4"><div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">Objects tracked</div><div className="font-display text-2xl font-extrabold text-zinc-900 mt-1">{total}</div></div>
        <div className="card p-4"><div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">Ready for cutover</div><div className="font-display text-2xl font-extrabold text-green-700 mt-1">{ready}</div><div className="text-[10px] text-zinc-500">{total ? Math.round(ready * 100 / total) : 0}% of objects</div></div>
        <div className="card p-4"><div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">Avg error rate</div><div className={`font-display text-2xl font-extrabold mt-1 ${avgError > 3 ? "text-red-700" : avgError > 1 ? "text-amber-700" : "text-green-700"}`}>{avgError}%</div></div>
      </div>

      <div className="mb-5">
        <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-2">Migration objects</div>
        <EditableTable
          sectionKey="dataMigration"
          listKey="objects"
          testidPrefix="dm-obj"
          labelSingular="object"
          columns={[
            { key: "object", label: "Object", width: "180px" },
            { key: "source", label: "Source system", width: "140px" },
            { key: "target", label: "Target (S/4)", width: "120px" },
            { key: "records", label: "Records", type: "number", width: "100px" },
            { key: "cycle", label: "Last cycle", type: "select", options: ["Extract", "Mock 1", "Mock 2", "Mock 3", "Cutover"], width: "110px" },
            { key: "errorPct", label: "Error %", type: "number", width: "80px" },
            { key: "status", label: "Status", type: "select", options: ["Not started", "In progress", "Reconciliation", "Sign-off", "Ready"], width: "130px" },
            { key: "owner", label: "Owner", width: "120px" },
          ]}
          emptyRow={() => ({ object: "", source: "", target: "S/4HANA", records: 0, cycle: "Extract", errorPct: 0, status: "Not started", owner: "" })}
        />
      </div>

      <div className="mb-5">
        <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-2">Data quality scorecard by source system</div>
        <EditableTable
          sectionKey="dataMigration"
          listKey="quality"
          testidPrefix="dq"
          labelSingular="source"
          columns={[
            { key: "source", label: "Source system", width: "180px" },
            { key: "completeness", label: "Completeness %", type: "number", width: "120px" },
            { key: "accuracy", label: "Accuracy %", type: "number", width: "120px" },
            { key: "duplicates", label: "Duplicates %", type: "number", width: "120px" },
            { key: "referentialIntegrity", label: "Ref. integrity %", type: "number", width: "140px" },
            { key: "notes", label: "Remediation notes" },
          ]}
          emptyRow={() => ({ source: "", completeness: 100, accuracy: 100, duplicates: 0, referentialIntegrity: 100, notes: "" })}
        />
      </div>
    </>
  );
}
